import { useQuery, useQueries } from "@tanstack/react-query";
import i18next from "i18next";
import { SplashScreen } from "../../components/splash-screen";
import { EmployeeSettings } from "../../models/employee-settings";
import { IsoCountryCode } from "../../models/user";
import {
	getSystems,
	getEmploymentSettings,
	getPermissions,
	getUser,
	getCompanyProperties,
} from "../../services/api/user-api";
import applicationInsights from "../../services/application-insights/application-insights";
import { setRaygunUser } from "../../services/raygun/raygun-helper";
import { QueryKeys } from "../../types/query-key-enum";
import { setUserLocale } from "../../utils/locale-helper";
import { UserContext } from "../user-context";
import { isApp, setLoggingMetadata } from "../../utils/app-helper";
import { isNullOrWhiteSpace } from "../../utils/string-helper";
import { useBffUser } from "../../hooks/use-bff-user";

type UserContextProviderProps = {
	children: React.ReactNode;
};

export default function UserContextProvider({ children }: UserContextProviderProps) {
	const bffUser = useBffUser();

	if (isApp() && !isNullOrWhiteSpace(bffUser.subject)) {
		setLoggingMetadata({
			subject: bffUser.subject!,
		});
	}

	const {
		data: systemData,
		isSuccess: isSuccessSystems,
		isLoading: isLoadingSystems,
		status: systemStatus,
	} = useQuery({
		queryKey: [QueryKeys.Systems],
		queryFn: () => getSystems(),
	});

	const {
		data: userData,
		isSuccess: isSuccessUser,
		isLoading: isLoadingUser,
		status: userStatus,
	} = useQuery({
		queryKey: [QueryKeys.User],
		queryFn: () => getUser(),
	});

	const userLang = userData?.locale;
	if (userLang) {
		if (userLang !== i18next.language) {
			i18next.changeLanguage(userLang).catch(() => null);
		}
	}

	if (userData) {
		setRaygunUser(userData);
		applicationInsights.setUser(userData?.subject, systemData?.[0]?.name);
	}

	const systems = (systemData ?? [])?.sort((a, b) =>
		a.commonName.localeCompare(b.commonName, i18next.language, { sensitivity: "base" }),
	);

	const useEmployeeSettingsQueries = useQueries({
		queries: systems.map((system) => {
			return {
				queryKey: [QueryKeys.EmployeeSettings, system.name],
				queryFn: () => getEmploymentSettings(system.name),
				refetchOnWindowFocus: false,
				enabled: isSuccessSystems,
			};
		}),
	});

	const employeeSettings = useEmployeeSettingsQueries
		.flatMap((sq) => sq.data)
		.filter((d): d is EmployeeSettings => !!d);

	const {
		data: permissionData,
		isLoading: isLoadingPermissions,
		status: permissionStatus,
	} = useQuery({
		queryKey: [QueryKeys.Permissions],
		queryFn: () => getPermissions(systems),
		enabled: isSuccessSystems && isSuccessUser,
	});

	const {
		data: companyPropertiesData,
		isLoading: isLoadingCompanyProperties,
		status: companyPropertiesStatus,
	} = useQuery({
		queryKey: [QueryKeys.CompanyInformationProperties],
		queryFn: () => getCompanyProperties(systems),
		enabled: isSuccessSystems && isSuccessUser,
	});

	const isLoading =
		isLoadingSystems ||
		isLoadingUser ||
		isLoadingPermissions ||
		isLoadingCompanyProperties ||
		bffUser.isLoading ||
		useEmployeeSettingsQueries.some((x) => x.isLoading);

	let value: UserContext = {
		systems: [],
		permissions: new Map(),
		employeeSettings: [],
		userLocale: setUserLocale(userLang),
		systemCountryCode: IsoCountryCode.SE,
		companyProperties: new Map(),
		...bffUser,
	};
	if (systemData && permissionData && userData && employeeSettings && companyPropertiesData) {
		value = {
			systems: systems,
			permissions: permissionData,
			user: userData,
			employeeSettings: employeeSettings,
			userLocale: setUserLocale(userLang),
			systemCountryCode: systems[0]?.countryCode ?? IsoCountryCode.SE,
			companyProperties: companyPropertiesData,
			...bffUser,
		};
	}

	if (
		permissionStatus === "error" ||
		systemStatus === "error" ||
		userStatus === "error" ||
		companyPropertiesStatus === "error" ||
		bffUser.status === "error" ||
		useEmployeeSettingsQueries.find((x) => x.status === "error")
	) {
		throw new Error("Failed fetch data");
	}

	return (
		<UserContext.Provider value={value}>
			{!isLoading && isSuccessUser ? children : <SplashScreen />}
		</UserContext.Provider>
	);
}
